import * as React from 'react'
import { CreateInvoiceDetail } from '../InvoiceDetailStore'
import {
  InputNumber,
  Popconfirm,
  Input,
  Button,
  Popover,
  Icon,
  message,
  Tooltip,
  Row,
  Table,
} from 'antd'
import { observer, inject } from 'mobx-react'
import {
  CreateInvoiceLine,
  IBaseProduct,
  API,
  INestedProduct,
  InvoiceStatus,
  IInvoiceLine,
} from '@getgreenline/homi-shared'
import '../../invoiceTable.scss'
import { InvoiceDetailBatchTable } from './InvoiceDetailBatchTable'
import { parseErrorMsg } from '../../../../../utilities/helpers'
import { AsyncProductInput } from '../../../../../components/GrInput/AsyncProductInput'
import { InvoiceDetailFeeTable } from './InvoiceDetailFeeTable'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { InvoiceBarcodeScan } from './InvoiceBarcodeScan'
import { EditCompletedCostModal } from './EditCompletedCostModal'
import { ExpandIconProps, SorterResult, ColumnProps } from 'antd/lib/table'
import { CostPerUnit } from './CostPerUnit'
import { CostTotal } from './CostTotal'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'
import { Link } from 'react-router'
import { Routes } from '../../../../../constants/Routes'
import { CustomFilterDropdown } from './CustomFilterDropdown'
import Highlighter from 'react-highlight-words'

interface Props {
  createInvoice: CreateInvoiceDetail
}

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface State {
  isOverrideWeightedAverage: boolean
  showEditCompletedCostModal: boolean
  expandedRowKeys: number[]
  selectedProduct?: INestedProduct
  filteredInfo: Partial<Record<keyof InvoiceDetailTableRecord, string[]>> | null
  sortedInfo: SorterResult<InvoiceDetailTableRecord> | null
  searchText: string
  searchedColumn?: keyof InvoiceDetailTableRecord
}

export interface InvoiceDetailTableRecord {
  key: number
  productNameAndSku: JSX.Element
  product: CreateInvoiceLine | IInvoiceLine | undefined
  qtyOrdered?: number | JSX.Element
  qtyReceived?: number | JSX.Element
  costPerUnit: JSX.Element
  total: JSX.Element
  lotNumber?: JSX.Element
  notes: string | JSX.Element
  options?: JSX.Element
}

/**
 * Reference for nested tables
 * https://stackblitz.com/edit/react-eydnk3?file=Hello.js
 */
@inject('currentCompanyStore')
@observer
export class InvoiceDetailTable extends React.Component<Props & InjectedProps, State> {
  private _expandedRowKeys = new Set<number>()

  private company = this.props.currentCompanyStore!.company!

  private inputMin = 0

  private lotNumberColumnIndex = 4

  constructor(props: Props) {
    super(props)

    this.state = {
      isOverrideWeightedAverage: false,
      showEditCompletedCostModal: false,
      expandedRowKeys: Array.from(this._expandedRowKeys.values()),
      selectedProduct: undefined,
      filteredInfo: null,
      sortedInfo: null,
      searchText: '',
      searchedColumn: undefined,
    }
  }

  setShowEditCompletedCostModal = (showEditCompletedCostModal: boolean) => {
    this.setState({ showEditCompletedCostModal })
  }

  onOverrideInvoiceLine = () =>
    this.setState({
      isOverrideWeightedAverage: true,
      showEditCompletedCostModal: false,
    })

  onEditCompletedCost = async (line: CreateInvoiceLine) => {
    if (!line.productId) {
      return
    }

    await this.getProduct(line.productId)
    this.setShowEditCompletedCostModal(!this.state.showEditCompletedCostModal)
  }

  getProduct = async (productId: string) => {
    try {
      const selectedProduct = await API.getProductById(this.company.id, productId)
      this.setState({ selectedProduct })
    } catch (error) {
      message.error(parseErrorMsg(error) || `An error occured when loading the product`)
    }
  }

  expandRowRender = (record: InvoiceDetailTableRecord) => {
    if (!record.product?.isBatchTracked) {
      return
    }

    /**
     * Lot addition & deletion changes are only visible if the nested batch table is abstracted as its own for some reason.
     */
    return (
      <InvoiceDetailBatchTable
        product={record.product}
        createInvoice={this.props.createInvoice}
        renderColumns={this.renderColumns}
      />
    )
  }

  getRowClassName = (record: InvoiceDetailTableRecord) => {
    return !record.product || record.product.batches.length === 0
      ? 'background-white invoice-detail-table--hide-expand'
      : 'background-white'
  }

  onExpand = (expanded: boolean, record: InvoiceDetailTableRecord) => {
    this._expandedRowKeys.has(record.key)
      ? this._expandedRowKeys.delete(record.key)
      : this._expandedRowKeys.add(record.key)

    this.setExpandedRowKeys()
  }

  setExpandedRowKeys = () =>
    this.setState({
      expandedRowKeys: Array.from(this._expandedRowKeys.values()),
    })

  /**
   *  Referernce to `<T extends unknown>
   * https://stackoverflow.com/questions/32308370/what-is-the-syntax-for-typescript-arrow-functions-with-generics
   */
  renderColumns = <T extends unknown>(columns: ColumnProps<T>[]): ColumnProps<T>[] => {
    const { isEditMode, isEditable } = this.props.createInvoice
    const optionsColumnAdded = columns[columns.length - 1].key === 'options'

    if (isEditMode && isEditable) {
      if (!optionsColumnAdded) {
        columns.splice(columns.length, 0, {
          title: '',
          dataIndex: 'options',
          key: 'options',
          width: 40,
          align: 'center',
        })
      }
    } else {
      if (optionsColumnAdded) {
        columns.splice(columns.length - 1, 1)
      }
    }

    return columns
  }

  onExpandedRowsChange = (expandedRowKeys: any[]) => {
    expandedRowKeys.forEach((key: number) => {
      this._expandedRowKeys.delete(key)
      this.setExpandedRowKeys()
    })
  }

  setCostPerUnit = async (companyId: number, product: IBaseProduct, line: CreateInvoiceLine) => {
    const costPerUnit = await API.checkCostPerUnit(companyId, product.id)
    if (!costPerUnit) {
      return
    }

    // Can set total to be this because the default quantity is 1
    line.costPerUnit = costPerUnit

    line.setTotal(costPerUnit * (line.quantity || 0))
    line.setPreviousCostPerUnit(costPerUnit)
  }

  renderCustomExpandIcon = (props: ExpandIconProps<InvoiceDetailTableRecord>) => {
    const isBatchTracked = props.record.product?.isBatchTracked || false

    if (!isBatchTracked) {
      return
    }

    return (
      <Button
        className='mr-2'
        icon={props.expanded ? 'minus' : 'plus'}
        size='small'
        onClick={(event) => props.onExpand(props.record, event)}
      />
    )
  }

  fillQuantityReceived = () => {
    this.props.createInvoice.lines.forEach((line) => {
      line.setQuantityReceived(line.quantity)
    })
    this.props.createInvoice.setIsEdited(true)
  }

  addProducts = async (products: IBaseProduct[]) => {
    for (const product of products) {
      const createdLine = this.props.createInvoice.addProduct(product)
      await this.setCostPerUnit(this.company.id, product, createdLine)
    }
  }

  searchTable = (
    dataIndex: keyof InvoiceDetailTableRecord,
    searchText: string,
    confirm?: () => void,
  ) => {
    if (confirm) {
      confirm()

      this.setState({
        searchText: searchText,
        searchedColumn: dataIndex,
      })
    }
  }

  clearSearchResult = (clearFilters?: () => void) => {
    if (clearFilters) {
      clearFilters()
      this.setState({ searchText: '', searchedColumn: undefined })
    }
  }

  get invoiceLines(): Array<CreateInvoiceLine | IInvoiceLine> {
    const { createInvoice } = this.props
    const { invoiceDetail, isEditMode } = createInvoice

    return isEditMode ? createInvoice.lines : invoiceDetail ? invoiceDetail.lines : []
  }

  get isOverrideWeightedAverageButtonVisible(): boolean {
    const status = this.props.createInvoice.invoiceDetail?.status
    const isWeightedAverage = this.company.inventoryCostOption === 'weightedAverage'

    return (
      isWeightedAverage &&
      status === InvoiceStatus.COMPLETED &&
      !this.state.isOverrideWeightedAverage
    )
  }

  get mainTableDataSource(): InvoiceDetailTableRecord[] {
    const { createInvoice } = this.props
    const { isEditMode, isEditable } = createInvoice
    const splitSearchWords = [...this.state.searchText.split(' ')]

    return this.invoiceLines.map((line, lineIndex: number) => {
      const totalCountedQuantity =
        line instanceof CreateInvoiceLine
          ? line.batchQuantitySum
          : line.batches.reduce((prev, curr) => prev + curr.quantity, 0)

      const totalExpectedQuantity = line.quantityReceived || 0
      const textClassName =
        totalCountedQuantity === totalExpectedQuantity && totalCountedQuantity !== 0
          ? 'text-success'
          : 'text-danger'

      return {
        key: lineIndex,
        productNameAndSku: (
          <span>
            {line.isProductDeleted && (
              <Popover content='This product was deleted.'>
                <Icon className='mr-2' type='exclamation-circle' style={{ color: 'red' }} />
              </Popover>
            )}

            <Link
              to={
                line.productId
                  ? Routes.EDIT_PRODUCT(this.company.id, line.parentProductId || line.productId)
                  : '#'
              }
              target='_blank'
            >
              <Highlighter
                highlightClassName='highlighted-text'
                searchWords={splitSearchWords}
                autoEscape
                textToHighlight={line.productName}
              />
            </Link>

            {line.sku && (
              <Row>
                <Highlighter
                  highlightClassName='highlighted-text'
                  searchWords={splitSearchWords}
                  autoEscape
                  textToHighlight={line.sku}
                />
              </Row>
            )}
          </span>
        ),
        product: line,
        qtyOrdered:
          isEditMode && line instanceof CreateInvoiceLine ? (
            <InputNumber
              min={this.inputMin}
              value={line.quantity}
              disabled={
                (line.isBatchTracked || !createInvoice.isCompletedInCurrentMonth) &&
                !!createInvoice.completeDate
              }
              onChange={(value) => {
                const cost =
                  (line.costPerUnit > 0 ? line.costPerUnit : line.previousCostPerUnit) || 0

                if (value !== undefined) {
                  line.setTotal(value * cost)
                }
                line.setQuantity(value)
                line.setPreviousCostPerUnit(cost)
                this.props.createInvoice.setIsEdited(true)
              }}
            />
          ) : (
            line.quantity
          ),
        qtyReceived:
          isEditMode && line instanceof CreateInvoiceLine ? (
            <InputNumber
              key={lineIndex}
              min={this.inputMin}
              disabled={
                (line.isBatchTracked || !createInvoice.isCompletedInCurrentMonth) &&
                !!createInvoice.completeDate
              }
              value={line.quantityReceived}
              onChange={(value) => {
                line.setQuantityReceived(value)
                this.props.createInvoice.setIsEdited(true)
              }}
            />
          ) : (
            line.quantityReceived
          ),
        lotNumber: line.isBatchTracked ? (
          <span className={textClassName}>
            {`${totalCountedQuantity} / ${totalExpectedQuantity}`}
          </span>
        ) : undefined,
        costPerUnit: (
          <CostPerUnit
            company={this.company}
            createInvoice={createInvoice}
            line={line}
            isOverrideWeightedAverage={this.state.isOverrideWeightedAverage}
            isOverrideWeightedAverageButtonVisible={this.isOverrideWeightedAverageButtonVisible}
            getProduct={this.getProduct}
            selectedProduct={this.state.selectedProduct}
            onEditCompletedCost={this.onEditCompletedCost}
          />
        ),
        total: (
          <CostTotal
            company={this.company}
            createInvoice={createInvoice}
            line={line}
            isOverrideWeightedAverage={this.state.isOverrideWeightedAverage}
            isOverrideWeightedAverageButtonVisible={this.isOverrideWeightedAverageButtonVisible}
            onEditCompletedCost={this.onEditCompletedCost}
          />
        ),
        notes:
          isEditMode && line instanceof CreateInvoiceLine ? (
            <Input.TextArea
              rows={1}
              value={line.notes || undefined}
              placeholder='Add a note'
              onChange={(e) => {
                line.setNotes(e.target.value)
                this.props.createInvoice.setIsEdited(true)
              }}
            />
          ) : (
            line.notes || '-'
          ),
        options: isEditable ? (
          <Popconfirm
            title='Sure to remove this product?'
            placement='left'
            onConfirm={() => createInvoice.removeLine(lineIndex)}
          >
            <Button icon='close' size='small' />
          </Popconfirm>
        ) : undefined,
      }
    })
  }

  summary = (): InvoiceDetailTableRecord[] => {
    let totalOrdered = 0
    let totalReceived = 0

    this.invoiceLines.forEach((line) => {
      totalReceived += line.quantityReceived || 0
      totalOrdered += line.quantity || 0
    })

    if (this.invoiceLines.length > 0 && !this.state.sortedInfo?.order) {
      return [
        {
          key: this.invoiceLines.length,
          product: undefined,
          productNameAndSku: <b>Total</b>,
          qtyOrdered: <b>{totalOrdered}</b>,
          qtyReceived: <b>{totalReceived}</b>,
          costPerUnit: <></>,
          total: <></>,
          notes: '',
        },
      ]
    }

    return []
  }

  get baseColumns(): ColumnProps<InvoiceDetailTableRecord>[] {
    const { sortedInfo, filteredInfo } = this.state

    const columns: ColumnProps<InvoiceDetailTableRecord>[] = [
      {
        title: 'Product',
        dataIndex: 'productNameAndSku',
        key: 'productNameAndSku',
        width: 250,
        align: 'left',
        filteredValue: filteredInfo?.productNameAndSku || null,
        onFilter: (searchText: string, record) => {
          if (!record.product) {
            return false
          }
          const lowerCasedProductName = record.product.productName.toLowerCase()
          const lowerCasedSku = (record.product.sku || '').toLowerCase()

          return searchText.split(' ').some((word: string) => {
            const lowerCasedWord = word.toLowerCase()

            return `${lowerCasedProductName} ${lowerCasedSku}`.includes(lowerCasedWord)
          })
        },
        filterDropdown: (filterDropdownProps) => (
          <CustomFilterDropdown<InvoiceDetailTableRecord>
            placeholder='Search by name or SKU'
            dataIndex='productNameAndSku'
            filterDropdownProps={filterDropdownProps}
            searchTable={this.searchTable}
            clearSearchResult={this.clearSearchResult}
          />
        ),
      },
      {
        title: (
          <>
            <span>Quantity</span>
            {this.props.createInvoice.isEditMode && (
              <Tooltip title='Individual units, not cases/boxes'>
                <Icon className='ml-2' type='exclamation-circle' />
              </Tooltip>
            )}
          </>
        ),
        children: [
          {
            title: 'Ordered',
            dataIndex: 'qtyOrdered',
            key: 'qtyOrdered',
            align: 'right',
            width: 90,
            sorter: (record1, record2) => {
              if (!record1.product || !record2.product) return 0

              return (record1.product.quantity || 0) - (record2.product.quantity || 0)
            },
            sortOrder:
              sortedInfo !== null && sortedInfo.columnKey === 'qtyOrdered' && sortedInfo.order,
          },
          {
            title: 'Received',
            dataIndex: 'qtyReceived',
            key: 'qtyReceived',
            align: 'right',
            width: 120,
            sorter: (record1, record2) => {
              if (!record1.product || !record2.product) return 0

              return (
                (record1.product.quantityReceived || 0) - (record2.product.quantityReceived || 0)
              )
            },
            sortOrder:
              sortedInfo !== null && sortedInfo.columnKey === 'qtyReceived' && sortedInfo.order,
            filters: [
              {
                text: 'Under-received',
                value: 'underReceived',
              },
              {
                text: 'Over-received',
                value: 'overReceived',
              },
            ],
            filterMultiple: false,
            filteredValue: filteredInfo?.qtyReceived || null,
            onFilter: (value, record) => {
              if (!record.product) return false

              switch (value) {
                case 'underReceived':
                  return (record.product.quantityReceived || 0) < (record.product.quantity || 0)
                case 'overReceived':
                  return (record.product.quantityReceived || 0) > (record.product.quantity || 0)
                default:
                  return true
              }
            },
          },
        ],
      },
      {
        title: 'Cost per unit',
        dataIndex: 'costPerUnit',
        key: 'costPerUnit',
        align: 'right',
        width: 140,
        sorter: (record1, record2) => {
          if (!record1.product || !record2.product) return 0

          const record1CostPerUnit =
            record1.product instanceof CreateInvoiceLine
              ? record1.product.costPerUnit
              : record1.product.total / (record1.product.quantity || 1)

          const record2CostPerUnit =
            record2.product instanceof CreateInvoiceLine
              ? record2.product.costPerUnit
              : record2.product.total / (record2.product.quantity || 1)

          return record1CostPerUnit - record2CostPerUnit
        },
        sortOrder:
          sortedInfo !== null && sortedInfo.columnKey === 'costPerUnit' && sortedInfo.order,
        filters: [
          {
            text: 'Negative margin',
            value: 'negativemMargin',
          },
        ],
        filterMultiple: true,
        filteredValue: filteredInfo?.costPerUnit || null,
        onFilter: (value, record) => {
          if (!record.product) {
            return false
          }
          const costPerUnit =
            record.product instanceof CreateInvoiceLine
              ? record.product.costPerUnit
              : record.product.total / (record.product.quantity || 1)

          return costPerUnit > (record.product.price || 0)
        },
      },
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        align: 'right',
        width: 110,
        sorter: (record1, record2) => {
          if (!record1.product || !record2.product) return 0
          return record1.product.total - record2.product.total
        },
        sortOrder: sortedInfo !== null && sortedInfo.columnKey === 'total' && sortedInfo.order,
      },
      {
        title: 'Notes',
        dataIndex: 'notes',
        key: 'notes',
        width: 120,
        filteredValue: filteredInfo ? filteredInfo.notes : null,
        onFilter: (value, record) => {
          if (!record.product) return false

          const lowerCasedNotes = (record.product.notes || '').toLowerCase()
          return lowerCasedNotes.includes(value.toLowerCase())
        },
        filterDropdown: (filterDropdownProps) => (
          <CustomFilterDropdown<InvoiceDetailTableRecord>
            placeholder='Search by notes'
            dataIndex='notes'
            filterDropdownProps={filterDropdownProps}
            searchTable={this.searchTable}
            clearSearchResult={this.clearSearchResult}
          />
        ),
      },
    ]

    const batchAddColumnAdded = columns[this.lotNumberColumnIndex].key === 'lotNumber'

    if (this.props.createInvoice.batchLinesExist) {
      if (!batchAddColumnAdded) {
        columns.splice(this.lotNumberColumnIndex, 0, {
          title: (
            <>
              <span>Lot #</span>
              <Tooltip title='Cannabis products come with lot numbers on the packaging. Record the numbers here for return/recall management'>
                <Icon className='ml-2' type='exclamation-circle' />
              </Tooltip>
            </>
          ),
          dataIndex: 'lotNumber',
          key: 'lotNumber',
          width: 110,
          filters: [
            {
              text: 'Unmatching lot quantities',
              value: 'unmatchingLotQuantities',
            },
          ],
          filteredValue: filteredInfo?.lotNumber || null,
          onFilter: (value, record) => {
            if (!record.product) return false

            const { isBatchTracked, batches, quantityReceived } = record.product
            const batchQuantitySum = batches.reduce((prev, curr) => prev + curr.quantity, 0)

            return isBatchTracked && batchQuantitySum !== (quantityReceived || 0)
          },
        })
      }
    } else {
      if (batchAddColumnAdded) {
        columns.splice(this.lotNumberColumnIndex, 1)
      }
    }

    return columns
  }

  get tableClassName(): string {
    const className = 'invoice-detail-table no-background-table mt-3 mb-3'

    if (this.invoiceLines.length > 0) {
      return className + ` show-horizontal-scrollbar`
    }

    return className
  }

  render() {
    const { createInvoice } = this.props
    const { lines, isEditMode, isEditable, isReturn } = createInvoice
    const { showEditCompletedCostModal, selectedProduct, sortedInfo, filteredInfo } = this.state

    return (
      <div>
        <Row>
          {lines.length > 0 && isEditMode && isEditable && !isReturn && (
            <BlazeButton
              buttonProps={{
                className: 'mt-3 mr-2',
                onClick: this.fillQuantityReceived,
              }}
              titleProps={{
                title: 'Fill quantity received',
              }}
            />
          )}

          {(filteredInfo !== null || sortedInfo !== null) && (
            <BlazeButton
              buttonProps={{
                className: 'mt-3',
                onClick: () =>
                  this.setState({
                    searchText: '',
                    searchedColumn: undefined,
                    filteredInfo: null,
                    sortedInfo: null,
                  }),
              }}
              titleProps={{
                title: 'Clear filters and sorters',
              }}
            />
          )}
        </Row>

        <Table<InvoiceDetailTableRecord>
          size='small'
          bordered
          className={this.tableClassName}
          pagination={false}
          columns={this.renderColumns<InvoiceDetailTableRecord>(this.baseColumns)}
          dataSource={this.mainTableDataSource.concat(this.summary())}
          rowClassName={this.getRowClassName}
          expandedRowKeys={this.state.expandedRowKeys}
          onExpand={this.onExpand}
          expandedRowRender={this.expandRowRender}
          onExpandedRowsChange={this.onExpandedRowsChange}
          expandIcon={this.renderCustomExpandIcon}
          expandIconColumnIndex={5}
          expandIconAsCell={false}
          // Reference: https://ant.design/components/table/#components-table-demo-reset-filter
          onChange={(pagination, filters, sorter, extra) =>
            this.setState({
              filteredInfo: filters,
              sortedInfo: sorter,
            })
          }
          scroll={{
            x: 'max-content',
          }}
        />

        {createInvoice.isEditMode && (
          <React.Fragment>
            <Row className='pt-3 pr-3 pl-3 background-white'>
              <Row>
                <label>
                  Add product
                  <Tooltip title='Search by name, SKU, or barcode'>
                    <Icon className='ml-2' type='exclamation-circle' />
                  </Tooltip>
                </label>
              </Row>

              <Row className='d-flex flex-wrap'>
                <AsyncProductInput
                  showSearch
                  mode='default'
                  placeholder='Select products'
                  className='mr-3 mb-3 w-100'
                  style={{ maxWidth: 400 }}
                  allowClear
                  productIdsToHide={this.props.createInvoice.lines.map(
                    (line) => line.productId || '',
                  )}
                  onChange={this.addProducts}
                />

                <InvoiceBarcodeScan
                  className='mb-3'
                  createInvoice={createInvoice}
                  setCostPerUnit={this.setCostPerUnit}
                />
              </Row>
            </Row>

            <InvoiceDetailFeeTable createInvoice={createInvoice} />
          </React.Fragment>
        )}

        {showEditCompletedCostModal && selectedProduct && (
          <EditCompletedCostModal
            companyId={this.company.id}
            product={selectedProduct}
            onHide={() => this.setShowEditCompletedCostModal(false)}
            onOverrideInvoiceLine={this.onOverrideInvoiceLine}
          />
        )}
      </div>
    )
  }
}
